import Delete from '@mui/icons-material/Delete';
import { Avatar, Box, Grid, IconButton } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IComposableDashboard,
  IComposableDashboardCollection,
  IPinnedDashboard,
  IPinnedDashboardCollection,
  IRecruiterTeamNameAutoCompleteOption,
} from 'wisestep-api';
import { WsTypography, WsButton, WsAutoComplete, WsDataGrid, ScrollableTabs } from 'wisestep-base';

import { WsTeamsFilter } from '../../../../components/filters/components';
import RecruiterSuggestions from '../../../../components/filters/components/RecruiterSuggestions';
import PrivateSvg from '../../../../images/PrivateLock.svg';
import PublicAccessSvg from '../../../../images/PublicAccess.svg';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { defaultRoles } from '../../../accessControl/AddRoleAccessControl';

interface IShareDashboardDialogProps {
  dashboard: IComposableDashboard | IComposableDashboardCollection;
  onCloseDialog: () => void;
  onSave: (dashboard: IComposableDashboard | IComposableDashboardCollection) => void;
  isAssignFlow: boolean;
  sharedToTeams: IRecruiterTeamNameAutoCompleteOption[];
  selectedPinnedDashboard: IPinnedDashboard | IPinnedDashboardCollection | undefined;
}

const sharedWith = ['Users', 'Roles', 'Teams'];
type FormDataType = Omit<
  IComposableDashboard | IComposableDashboardCollection,
  'sharedWithTeams'
> & {
  sharedWithTeams: IRecruiterTeamNameAutoCompleteOption[];
};

const ShareDashboardDialog = ({
  dashboard,
  onCloseDialog,
  onSave,
  isAssignFlow = false,
  sharedToTeams,
  selectedPinnedDashboard,
}: IShareDashboardDialogProps) => {
  const sharedWithTeamsRef = useRef<IRecruiterTeamNameAutoCompleteOption[]>(sharedToTeams);
  const methods = useForm<FormDataType>({
    defaultValues: {
      sharedWithTeams: [],
      sharedWithRecruiters: [],
      sharedWithRoles: [],
    },
  });
  const { handleSubmit } = methods;
  const [accessLevel, setAccessLevel] = useState<'public' | 'restricted' | 'shared'>(
    (dashboard.accessLevel as any) || 'public'
  );
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [state, setState] = useState(false);
  const refresh = () => setState((prev) => !prev);

  const tableData = useMemo(() => {
    const field =
      selectedTab === 0
        ? 'sharedWithRecruiters'
        : selectedTab === 1
        ? 'sharedWithRoles'
        : 'sharedWithTeams';
    const headerName = sharedWith[selectedTab];
    const columns = [
      {
        field,
        headerName,
        flex: 1,
      },
      {
        field: 'action',
        headerName: '',
        flex: 0.1,
        renderCell: (params) => (
          <IconButton
            onClick={() => {
              dashboard[field] = dashboard[field].filter((item) => item !== params.row.id);
              if (field == 'sharedWithTeams') {
                sharedWithTeamsRef.current = sharedWithTeamsRef.current.filter(
                  (item) => item.id !== params.row.id
                );
              }
              refresh();
            }}
          >
            <Delete />
          </IconButton>
        ),
      },
    ];
    const rows = (
      field == 'sharedWithTeams' ? sharedWithTeamsRef.current : dashboard[field] ?? []
    ).map((entity) => ({
      id: field == 'sharedWithTeams' ? entity.id : entity,
      [field]: field == 'sharedWithTeams' ? entity.name : entity,
    }));
    return { columns, rows };
  }, [dashboard, selectedTab, state]);

  const onSubmit = (data: FormDataType) => {
    const updatedDashboardState = { ...dashboard };
    updatedDashboardState.accessLevel = accessLevel;

    if (accessLevel == 'restricted' && !isAssignFlow) {
      updatedDashboardState.sharedWithTeams =
        updatedDashboardState.sharedWithRecruiters =
        updatedDashboardState.sharedWithRoles =
          [];
    } else {
      updatedDashboardState.sharedWithTeams = Array.from(
        new Set<string>([
          ...updatedDashboardState.sharedWithTeams,
          ...data.sharedWithTeams.map((team) => team.id),
        ])
      );
      updatedDashboardState.sharedWithRecruiters = Array.from(
        new Set<string>([
          ...updatedDashboardState.sharedWithRecruiters,
          ...data.sharedWithRecruiters,
        ])
      );
      updatedDashboardState.sharedWithRoles = Array.from(
        new Set<string>([...updatedDashboardState.sharedWithRoles, ...data.sharedWithRoles])
      );
    }
    onSave(updatedDashboardState);
  };

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <FormProvider {...methods}>
        <Grid container rowGap={2} p={2} component="form" onSubmit={handleSubmit(onSubmit)}>
          <WsTypography>Choose Access Level</WsTypography>
          <Grid item xs={12} display="flex" columnGap={2} alignItems="center">
            <WsButton variant="outlined" type="button" onClick={() => setAccessLevel('public')}>
              Public
            </WsButton>
            <WsButton variant="outlined" type="button" onClick={() => setAccessLevel('restricted')}>
              Private
            </WsButton>
            <WsButton variant="outlined" type="button" onClick={() => setAccessLevel('shared')}>
              Shared
            </WsButton>
          </Grid>
          {accessLevel === 'shared' && (
            <>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                  bgcolor: '#F0F2F3',
                  border: '1px solid #E0E0E0',
                  borderRadius: 2,
                }}
              >
                <WsTypography>{isAssignFlow ? 'Assign to' : 'Share With'}</WsTypography>
                <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                  <Grid item xs={2}>
                    <WsTypography>Teams</WsTypography>
                  </Grid>
                  <Grid xs={10}>
                    <WsTeamsFilter
                      name="sharedWithTeams"
                      defaultObjects={sharedWithTeamsRef.current}
                      defaultValue={sharedWithTeamsRef.current?.map((team) => team.name) ?? []}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                  <Grid item xs={2}>
                    <WsTypography>Users</WsTypography>
                  </Grid>
                  <Grid xs={10}>
                    <RecruiterSuggestions
                      name="sharedWithRecruiters"
                      multiple={true}
                      defaultValue={
                        (isAssignFlow
                          ? selectedPinnedDashboard?.users
                          : dashboard?.sharedWithRecruiters) ?? []
                      }
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container display="flex" alignItems="center" pt={2}>
                  <Grid item xs={2}>
                    <WsTypography>Select Role</WsTypography>
                  </Grid>
                  <Grid xs={10}>
                    <WsAutoComplete
                      name="sharedWithRoles"
                      optionsInStringArray={defaultRoles?.map((role) => role.label)}
                      multiple={true}
                      initialValue={
                        (isAssignFlow
                          ? selectedPinnedDashboard?.roles
                          : dashboard?.sharedWithRoles) ?? []
                      }
                      onTagsChange={(tags: string[]) => methods.setValue('sharedWithRoles', tags)}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ p: 2, border: '1px solid #E0E0E0', borderRadius: 2 }}>
                {!isAssignFlow && (
                  <ScrollableTabs
                    scrollList={sharedWith.map((level) => ({ tabName: level }))}
                    getTabIndex={(index) => setSelectedTab(index)}
                  />
                )}
                <WsDataGrid
                  rows={tableData.rows}
                  columns={tableData.columns}
                  page={0}
                  pageSize={5}
                  autoHeight
                />
              </Grid>
            </>
          )}
          {accessLevel == 'public' && (
            <Grid item xs={12}>
              <Avatar
                variant="square"
                sx={{ width: '100%', height: '100%' }}
                src={PublicAccessSvg}
              />
            </Grid>
          )}
          {accessLevel == 'restricted' && (
            <Grid item xs={12}>
              <Avatar variant="square" sx={{ width: '100%', height: '100%' }} src={PrivateSvg} />
            </Grid>
          )}
          <Box
            mr={0}
            ml="auto"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}
            columnGap={3}
          >
            <WsButton onClick={onCloseDialog} variant="outlined">
              Cancel
            </WsButton>
            <WsButton variant="contained" type="submit">
              Save
            </WsButton>
          </Box>
        </Grid>
      </FormProvider>
    </ErrorBoundary>
  );
};

export default ShareDashboardDialog;
